import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { MoneyText } from '@/components/ui/MoneyText'
import { Table, TBody, Td, THead, Th, Tr } from '@/components/ui/Table'
import { KIND_LABELS } from '@/data/income'
import type { FundBalance } from '@/data/income'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'

const dash = <span className="text-muted">—</span>

function TypePill({ b }: { b: FundBalance }) {
  const passedOn = b.destination === 'passed_on'
  return <Badge tone={passedOn ? 'info' : 'success'}>{passedOn ? 'Passed on' : 'Stays with the church'}</Badge>
}

/**
 * Every fund with what it has received, sent on and still holds. A table where there is room for
 * one; on a phone each fund becomes a small card with the same three figures labelled, so nothing
 * needs scrolling sideways to be read.
 */
export function FundsTable({ funds, canWrite, onSend }: { funds: FundBalance[]; canWrite: boolean; onSend: (fundId: string) => void }) {
  return (
    <>
      <div className="hidden sm:block">
        <Table label="Funds">
          <THead>
            <Tr className="hover:bg-transparent">
              <Th>Fund</Th>
              <Th numeric>Counted</Th>
              <Th numeric>Sent on</Th>
              <Th numeric>Still held</Th>
              <Th><span className="sr-only">Actions</span></Th>
            </Tr>
          </THead>
          <TBody>
            {funds.map((b) => {
              const passedOn = b.destination === 'passed_on'
              return (
                <Tr key={b.fundId}>
                  <Td>
                    <p className="font-medium text-ink">{b.name}{b.archived ? ' (retired)' : ''}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-label text-muted">
                      {KIND_LABELS[b.kind]}
                      <TypePill b={b} />
                    </p>
                  </Td>
                  <Td numeric><MoneyText ngwee={b.receivedNgwee} /></Td>
                  <Td numeric>{passedOn ? <MoneyText ngwee={b.sentNgwee} /> : dash}</Td>
                  <Td numeric>
                    {passedOn ? <MoneyText ngwee={b.heldNgwee} strongZero className="font-semibold" /> : dash}
                    {passedOn && b.heldNgwee < 0 && <span className="mt-1 block"><Badge tone="warning">More sent than counted</Badge></span>}
                  </Td>
                  <Td className="text-right">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {canWrite && passedOn && <Button variant="secondary" size="sm" onClick={() => onSend(b.fundId)}>Record money sent</Button>}
                      {b.kind !== 'tithe' && <OwnerDeleteButton table="income_fund" id={b.fundId} what="fund" />}
                    </div>
                  </Td>
                </Tr>
              )
            })}
          </TBody>
        </Table>
      </div>

      <ul className="divide-y divide-line sm:hidden" aria-label="Funds">
        {funds.map((b) => {
          const passedOn = b.destination === 'passed_on'
          return (
            <li key={b.fundId} className="py-4 first:pt-0 last:pb-0">
              <p className="text-card font-semibold text-ink">{b.name}{b.archived ? ' (retired)' : ''}</p>
              <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-label text-muted">
                {KIND_LABELS[b.kind]}
                <TypePill b={b} />
              </p>
              <dl className="mt-3 grid grid-cols-3 gap-2 text-body">
                <div>
                  <dt className="text-label text-muted">Counted</dt>
                  <dd className="tabular-nums"><MoneyText ngwee={b.receivedNgwee} /></dd>
                </div>
                <div>
                  <dt className="text-label text-muted">Sent on</dt>
                  <dd className="tabular-nums">{passedOn ? <MoneyText ngwee={b.sentNgwee} /> : dash}</dd>
                </div>
                <div>
                  <dt className="text-label text-muted">Still held</dt>
                  <dd className="font-semibold tabular-nums">{passedOn ? <MoneyText ngwee={b.heldNgwee} strongZero /> : dash}</dd>
                </div>
              </dl>
              {passedOn && b.heldNgwee < 0 && <p className="mt-2"><Badge tone="warning">More sent than counted</Badge></p>}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {canWrite && passedOn && <Button variant="secondary" size="sm" onClick={() => onSend(b.fundId)}>Record money sent</Button>}
                {b.kind !== 'tithe' && <OwnerDeleteButton table="income_fund" id={b.fundId} what="fund" />}
              </div>
            </li>
          )
        })}
      </ul>
    </>
  )
}
